import { api } from 'api';
import ClearIcon from 'assets/ver2/icons/close-black-icon.svg';
import NoResultImage from 'assets/ver2/icons/no-result-icon.svg';
import SearchIcon from 'assets/ver2/icons/search-icon.svg';
import Drawer from 'components/common/Drawer';
import { DESKTOP_MIN_WIDTH } from 'constants';
import { searchTextValid } from 'helpers';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useExchangeRatesSelected from 'hooks/useExchangeRatesSelected';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import { isArray, isEmpty, isFunction } from 'lodash';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { SET_REMITTANCE_RATES_SELECTED } from 'store/action';
import styled from 'styled-components';
import { arrayUtils, stringUtils } from 'utils';
import ExchangeRateAccordionItem from '../ExchangeRateItem/ver4';

const SEARCH_NAME_FIELD = 'drawer-search-country';

const SelectRateOfCountryToSendDrawer = ({
  countries,
  remittanceRatesData,
  NavBarToggle,
  onClose,
  ref,
}) => {
  const { t } = useLang();
  const { setState } = useStore();
  const { isDesktop } = useDetectDevice();

  const [searchValue, setSearchValue] = useState('');
  const [countriesSearch, setCountriesSearch] = useState(null);
  const [currentSelected, setCurrentSelected] = useState(null);

  const drawerRef = useRef();

  const isSearchInvalid = !searchTextValid(searchValue);
  const isSearchEmpty = searchValue && isEmpty(countriesSearch);

  countries = countries?.filter(
    (country) => !Object.keys(remittanceRatesData)?.includes(country?.code)
  );
  const countriesData = countriesSearch || countries || null;
  const countriesTransform = countriesData;

  const { getRemittanceRatesSelected, closeRemittanceRatesSelected } =
    useExchangeRatesSelected();

  useEffect(() => {
    if (isEmpty(currentSelected)) return;
    const timer = setInterval(() => {
      const currentRatesLatest =
        getRemittanceRatesSelected(currentSelected)?.[currentSelected.code];
      setState({
        action: SET_REMITTANCE_RATES_SELECTED,
        payload: currentRatesLatest,
      });
    }, [2000]);
    return () => {
      clearInterval(timer);
    };
  }, [currentSelected]);

  const handleOnToggleNavbar = () => {
    drawerRef?.current?.handleOnToggleNavbar();
  };
  useImperativeHandle(ref, () => ({
    isShowNavbar: ref?.current?.isShowNavbar,
    handleOnToggleNavbar,
  }));

  const handleOnResetCurrentSelected = () => {
    closeRemittanceRatesSelected(currentSelected);
    setCurrentSelected(null);
    setState({
      action: SET_REMITTANCE_RATES_SELECTED,
      payload: null,
    });
  };
  const handleOnClose = (onClose) => {
    handleOnResetCurrentSelected();
    setSearchValue('');
    setCountriesSearch(null);
    if (isFunction(onClose)) {
      onClose();
    }
  };
  useEffect(() => {
    return () => {
      handleOnClose();
    };
  }, []);

  const renderCountriesAccordion = (data) => {
    if (isEmpty(data) && !isArray(data)) return null;
    if (arrayUtils.isArrayEmpty(data)) {
      return <EmptyList>{t('form_text_no_result')}</EmptyList>;
    }
    return (
      <AccordionStyled>
        {data?.map((item, index) => {
          const isActive = currentSelected?.code === item.code;
          const getEventKey = () => {
            if (searchValue) return `s-${index}`;
            return index;
          };
          const handleCountryItemOnClick = async () => {
            if (currentSelected?.code === item.code) {
              handleOnResetCurrentSelected();
              return;
            }
            try {
              let newItem = {
                ...item,
                currencies: [],
              };
              const response = await api.getCurrenciesSupportedByCountry(
                item.code
              );
              newItem = {
                ...newItem,
                currencies: response?.data || [],
              };
              const currentRates =
                getRemittanceRatesSelected(newItem)?.[newItem.code];
              setState({
                action: SET_REMITTANCE_RATES_SELECTED,
                payload: currentRates,
              });
              setCurrentSelected(newItem);
            } catch (error) {
              setCurrentSelected(newItem);
              console.error(error?.message);
            }
          };
          return (
            <ExchangeRateAccordionItem
              key={item.id}
              eventKey={getEventKey()}
              country={item.code}
              countryName={item.name}
              isActive={isActive}
              onClick={handleCountryItemOnClick}
            />
          );
        })}
      </AccordionStyled>
    );
  };
  const renderBodyDrawerContent = () => {
    if (isSearchInvalid)
      return (
        <SearchErrorText role="alert">
          {t('form_msg_search_validation')}
        </SearchErrorText>
      );
    if (isSearchEmpty)
      return (
        <NoResult>
          <img
            src={NoResultImage}
            width={isDesktop ? 150 : 150}
            height={isDesktop ? 150 : 150}
            alt=""
          />
          <span
            dangerouslySetInnerHTML={{
              __html: stringUtils.stringTransform(
                t('label_no_result_search_desc')
              ),
            }}
          />
        </NoResult>
      );
    if (isEmpty(countriesTransform)) return null;
    return (
      <CountriesList>
        {renderCountriesAccordion(countriesTransform)}
      </CountriesList>
    );
  };

  const handleCountriesSearch = (e) => {
    const { value } = e.target;
    setSearchValue(value);

    handleOnResetCurrentSelected();

    if (!value) {
      setCountriesSearch(countries);
      return;
    }

    const newCountriesSearch = countries?.filter(
      (item) =>
        (item?.['name']?.toLowerCase()?.includes(value?.toLowerCase()) ||
          item?.['code']?.toLowerCase()?.includes(value?.toLowerCase())) &&
        item
    );
    setCountriesSearch(newCountriesSearch);
  };
  const handleOnClearSearchInput = () => {
    setSearchValue('');
    setCountriesSearch(null);
    handleOnResetCurrentSelected();
  };
  const renderSearchInputIcon = () => {
    if (!searchValue)
      return (
        <SearchIconStyled
          src={SearchIcon}
          width={20}
          height={20}
          alt=""
          onClick={() => {}}
        />
      );
    return (
      <SearchIconStyled
        src={ClearIcon}
        width={20}
        height={20}
        alt=""
        style={{
          cursor: 'pointer',
        }}
        onClick={handleOnClearSearchInput}
      />
    );
  };
  const renderSearchInpurField = () => (
    <SearchCountryStyled>
      <SearchInput
        name={SEARCH_NAME_FIELD}
        type="text"
        value={searchValue || ''}
        placeholder={t('label_search_input')}
        onChange={handleCountriesSearch}
      />
      {renderSearchInputIcon()}
    </SearchCountryStyled>
  );

  const getDrawerStyle = () => {
    if (isDesktop) return {};
    return {
      display: 'flex',
      justifyContent: 'center',
    };
  };

  return (
    <Drawer
      ref={drawerRef}
      navTitle={t('label_all_countries')}
      NavBarToggle={NavBarToggle}
      ContentBody={
        <CountriesWrap>
          {renderSearchInpurField()}
          {renderBodyDrawerContent()}
        </CountriesWrap>
      }
      onClose={() => handleOnClose(onClose)}
      drawerStyle={getDrawerStyle()}
      offcanvasBodyStyle={{ background: '#FCFCFC' }}
      navbarToggleStyle={{ width: 'fit-content' }}
    />
  );
};

const CountriesWrap = styled.div`
  padding: 0;
  border: none;
  width: 100%;
  min-height: 44px;
  height: 100%;
  background: #fff;
`;
const CountriesList = styled.div`
  padding: 0;
  border: none;
  width: 100%;
  background: #fcfcfc;
`;
const AccordionStyled = styled(Accordion)`
  width: 100%;
  border: none;
  margin-bottom: 8px;
`;

const EmptyList = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;

  margin: 0px;
  padding: 0px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const SearchCountryStyled = styled.div`
  position: relative;
  width: calc(100% + 32px);
  margin-inline: -16px;
  margin-top: -16px;
  padding-inline: 16px;
  padding-top: 16px;
  background: #fcfcfc;
  height: 80px;
  padding-bottom: 16px;
`;
const SearchInput = styled.input`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;

  color: var(--ds-c-grey-dark);
  border: none;
  border-radius: 8px;
  background: #f3f4f6;
  width: 100%;
  height: 44px;
  padding: 10px;
  padding-left: 34px;
  padding-right: 10px;
  float: left;
  margin-right: 0px;
  margin-bottom: 0px;

  &:focus,
  &:focus-visible {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: #7a8189;
    text-transform: initial;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
    width: 100%;
  }
`;
const SearchIconStyled = styled.img`
  position: absolute;
  top: 28px;
  left: 26px;
  user-select: none;
  filter: invert(52%) sepia(11%) saturate(325%) hue-rotate(171deg)
    brightness(94%) contrast(81%);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    left: 26px;
  }
`;

const SearchErrorText = styled.p`
  clear: both;
  margin: 0;
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-red);
`;

const NoResult = styled.div`
  display: grid;
  justify-items: center;
  position: relative;
  margin-top: 64px;

  & span {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    text-align: center;

    color: var(--ds-c-grey-dark);
    margin-bottom: 24px;
  }
`;

export default SelectRateOfCountryToSendDrawer;
